import React, { useEffect, useState } from 'react';
import { getLeads, createLead, updateLead, deleteLead, addNote, draftEmail, scoreLead } from '../api/client';
import client from '../api/client';
import { C, Btn, Tag, Toast, Spinner, initials, useNotify } from '../utils/styles';
import CsvImporter from '../components/CsvImporter';

const STAGES  = ['New','Contacted','Qualified','Proposal','Won','Lost'];
const SOURCES = ['Google Ads','Facebook Ads','Referral','Organic','Cold Outreach','WhatsApp','Other'];
const STAGE_COLORS = { New:C.primary, Contacted:C.purple, Qualified:C.warning, Proposal:C.green, Won:C.success, Lost:C.danger };
const scoreColor = s => s>=80 ? C.success : s>=60 ? C.warning : C.danger;
const CSV_HEADERS = ['first_name','last_name','email','phone','company','source','stage','value','tags','created_at'];
const fmtDate = (iso) => { if (!iso) return '—'; return new Date(iso).toLocaleDateString('en-IN',{ day:'numeric', month:'short', year:'numeric' }); };
const displayName = (l) => [l.first_name, l.last_name].filter(Boolean).join(' ') || l.name || '—';

export default function Leads() {
  const [leads,        setLeads]        = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [search,       setSearch]       = useState('');
  const [stageFilter,  setStageFilter]  = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [tagFilter,    setTagFilter]    = useState('');
  const [dndFilter,    setDndFilter]    = useState(''); // '' | 'dnd' | 'active'
  const [selLead,      setSelLead]      = useState(null);
  const [editMode,     setEditMode]     = useState(false);
  const [editData,     setEditData]     = useState({});
  const [showAdd,      setShowAdd]      = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [note,         setNote]         = useState('');
  const [aiDraft,      setAiDraft]      = useState('');
  const [aiLoading,    setAiLoading]    = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [addSaving,    setAddSaving]    = useState(false);
  const [dndLoading,   setDndLoading]   = useState(false);
  const [selectedIds,  setSelectedIds]  = useState([]);
  const [bulkTag,      setBulkTag]      = useState('');
  const [showBulkTag,  setShowBulkTag]  = useState(false);
  const [notification, notify]          = useNotify();
  const [newLead, setNewLead] = useState({
    first_name:'', last_name:'', email:'', phone:'', company:'', source:'Google Ads', stage:'New', value:'', tags:''
  });

  const fetchLeads = async () => {
    try {
      const { data } = await getLeads({ search, stage:stageFilter, limit:500 });
      setLeads(data.leads||[]);
    } catch { notify('Failed to load leads','error'); }
    finally { setLoading(false); }
  };
  const fetchCustomFields = async () => {
    try { const { data } = await client.get('/api/contacts/custom-fields'); setCustomFields(data||[]); } catch {}
  };
  useEffect(() => { fetchLeads(); fetchCustomFields(); }, [search, stageFilter]); // eslint-disable-line

  const allTags = [...new Set(leads.flatMap(l => l.tags||[]))].sort();
  const dndCount = leads.filter(l => l.dnd).length;

  const filtered = leads.filter(l => {
    if (sourceFilter && l.source !== sourceFilter) return false;
    if (tagFilter    && !(l.tags||[]).includes(tagFilter)) return false;
    if (dndFilter === 'dnd'    && !l.dnd) return false;
    if (dndFilter === 'active' &&  l.dnd) return false;
    return true;
  });

  const toggleSelect    = (id, e) => { e.stopPropagation(); setSelectedIds(p=>p.includes(id)?p.filter(x=>x!==id):[...p,id]); };
  const toggleSelectAll = () => setSelectedIds(selectedIds.length===filtered.length?[]:filtered.map(l=>l.id));
  const isSelected      = id => selectedIds.includes(id);

  // ── Add lead ───────────────────────────────────────────────
  const handleAdd = async () => {
    if (!newLead.first_name) return notify('First name is required','error');
    setAddSaving(true);
    try {
      await createLead({
        ...newLead,
        value: parseFloat(newLead.value)||0,
        tags:  newLead.tags ? newLead.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
      });
      notify('Lead added!');
      setShowAdd(false);
      setNewLead({ first_name:'', last_name:'', email:'', phone:'', company:'', source:'Google Ads', stage:'New', value:'', tags:'' });
      fetchLeads();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed';
      if (err.response?.data?.duplicate) notify(`${msg} — "${err.response.data.existing_name}". Open their record to update instead.`, 'error');
      else notify(msg, 'error');
    } finally { setAddSaving(false); }
  };

  const handleStageChange = async (id, stage, e) => {
    e && e.stopPropagation();
    try {
      await updateLead(id, { stage });
      setLeads(leads.map(l=>l.id===id?{...l,stage}:l));
      if (selLead?.id===id) setSelLead({...selLead,stage});
      notify(`Moved to ${stage}`);
    } catch { notify('Failed to update stage','error'); }
  };

  const handleDelete = async (id, e) => {
    e && e.stopPropagation();
    if (!window.confirm('Delete this lead?')) return;
    try {
      await deleteLead(id);
      setLeads(leads.filter(l=>l.id!==id));
      if (selLead?.id===id) closeModal();
      setSelectedIds(p=>p.filter(x=>x!==id));
      notify('Lead deleted');
    } catch { notify('Failed to delete','error'); }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return notify('No contacts selected','error');
    if (!window.confirm(`Delete ${selectedIds.length} contacts?`)) return;
    try {
      await Promise.all(selectedIds.map(id=>deleteLead(id)));
      setLeads(leads.filter(l=>!selectedIds.includes(l.id)));
      if (selectedIds.includes(selLead?.id)) closeModal();
      notify(`${selectedIds.length} contacts deleted`);
      setSelectedIds([]);
    } catch { notify('Bulk delete failed','error'); }
  };

  const handleBulkTag = async () => {
    if (!selectedIds.length||!bulkTag.trim()) return notify('Select contacts and enter a tag','error');
    const newTags = bulkTag.split(',').map(t=>t.trim()).filter(Boolean);
    try {
      await Promise.all(selectedIds.map(async id => {
        const lead = leads.find(l=>l.id===id);
        await updateLead(id,{ tags:[...new Set([...(lead?.tags||[]),...newTags])] });
      }));
      setLeads(leads.map(l=>selectedIds.includes(l.id)?{...l,tags:[...new Set([...(l.tags||[]),...newTags])]}:l));
      notify(`Tags added to ${selectedIds.length} contacts`);
      setBulkTag(''); setShowBulkTag(false); setSelectedIds([]);
    } catch { notify('Bulk tag failed','error'); }
  };

  const handleExport = () => {
    const toExport = selectedIds.length ? leads.filter(l=>selectedIds.includes(l.id)) : filtered;
    const cfKeys   = customFields.map(f=>f.field_key);
    const headers  = [...CSV_HEADERS,...cfKeys];
    const rows     = toExport.map(l=>[
      l.first_name||'', l.last_name||'', l.email||'', l.phone||'',
      l.company||'', l.source||'', l.stage||'', l.value||0,
      (l.tags||[]).join('|'), fmtDate(l.created_at),
      ...cfKeys.map(()=>''),
    ]);
    const csv = [headers,...rows].map(r=>r.map(v=>`"${(v||'').toString().replace(/"/g,'""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv],{type:'text/csv'}));
    const a   = document.createElement('a'); a.href=url; a.download='dx-contacts.csv'; a.click();
    URL.revokeObjectURL(url);
    notify(`Exported ${toExport.length} contacts`);
  };

  // ── DND toggle ─────────────────────────────────────────────
  const handleToggleDnd = async (lead, enableDnd) => {
    if (enableDnd) {
      if (!window.confirm(`Enable DND for ${displayName(lead)}? They will be excluded from all future campaigns.`)) return;
    } else {
      if (!window.confirm(`Remove DND for ${displayName(lead)}? They will be included in future campaigns again.`)) return;
    }
    setDndLoading(true);
    try {
      const { data } = await client.patch(`/api/email/leads/${lead.id}/dnd`, {
        dnd:    enableDnd,
        reason: enableDnd ? 'Manually set by admin' : null,
      });
      const updated = { ...lead, dnd: data.lead.dnd, dnd_reason: data.lead.dnd_reason };
      setLeads(leads.map(l => l.id === lead.id ? updated : l));
      if (selLead?.id === lead.id) setSelLead(updated);
      notify(data.message);
    } catch (err) { notify(err.response?.data?.error || 'Failed to update DND', 'error'); }
    finally { setDndLoading(false); }
  };

  const openModal  = (l) => { setSelLead(l); setEditMode(false); setAiDraft(''); setNote(''); };
  const closeModal = () => { setSelLead(null); setEditMode(false); setAiDraft(''); setNote(''); };

  const handleEditOpen = () => {
    setEditData({
      first_name: selLead.first_name || '',
      last_name:  selLead.last_name  || '',
      email:      selLead.email      || '',
      phone:      selLead.phone      || '',
      company:    selLead.company    || '',
      source:     selLead.source     || 'Google Ads',
      stage:      selLead.stage      || 'New',
      value:      selLead.value      || '',
      tags:       (selLead.tags||[]).join(', '),
      notes:      selLead.notes      || '',
    });
    setEditMode(true);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        value: parseFloat(editData.value)||0,
        tags:  editData.tags ? editData.tags.split(',').map(t=>t.trim()).filter(Boolean) : [],
      };
      await updateLead(selLead.id, payload);
      const updated = { ...selLead, ...payload, name:[payload.first_name, payload.last_name].filter(Boolean).join(' ') };
      setLeads(leads.map(l=>l.id===selLead.id?updated:l));
      setSelLead(updated);
      setEditMode(false);
      notify('Contact updated!');
    } catch (err) {
      notify(err.response?.data?.error || 'Failed', 'error');
    } finally { setSaving(false); }
  };

  const handleAddNote    = async () => { if (!note.trim()||!selLead) return; try { await addNote(selLead.id,note); setNote(''); notify('Note added!'); } catch { notify('Failed','error'); } };
  const handleDraftEmail = async () => { setAiLoading(true); setAiDraft(''); try { const {data}=await draftEmail(selLead.id,'follow up'); setAiDraft(data.draft); } catch { notify('AI draft failed','error'); } finally { setAiLoading(false); } };
  const handleRescore    = async () => { setAiLoading(true); try { const {data}=await scoreLead(selLead.id); setLeads(leads.map(l=>l.id===selLead.id?{...l,ai_score:data.ai_score}:l)); setSelLead({...selLead,ai_score:data.ai_score}); notify(`Rescored to ${data.ai_score}`); } catch { notify('Rescore failed','error'); } finally { setAiLoading(false); } };

  const activeFilters = [stageFilter, sourceFilter, tagFilter, dndFilter].filter(Boolean).length;

  if (loading) return <Spinner />;

  const inp = { width:'100%', padding:'8px 12px', borderRadius:8, border:`1px solid ${C.border}`, fontSize:13, outline:'none', boxSizing:'border-box', marginTop:4 };

  return (
    <div>
      <Toast {...notification} />

      {showImporter && (
        <CsvImporter customFields={customFields} onComplete={fetchLeads} onClose={()=>setShowImporter(false)} />
      )}

      {/* ══ Add Lead Modal ════════════════════════════════════ */}
      {showAdd && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={()=>setShowAdd(false)}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:580,boxShadow:'0 25px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'20px 24px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{display:'flex',alignItems:'center',gap:10}}>
                <i className="ti ti-user-plus" style={{fontSize:20,color:C.primary}}/>
                <span style={{fontWeight:700,fontSize:16,color:C.dark}}>New Contact</span>
              </div>
              <button onClick={()=>setShowAdd(false)} style={{background:'#f3f4f6',border:'none',borderRadius:8,padding:'6px 10px',cursor:'pointer',color:C.muted,fontSize:18,lineHeight:1}}><i className="ti ti-x"/></button>
            </div>
            <div style={{padding:'20px 24px',display:'grid',gap:14}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>First Name *</label><input style={inp} placeholder="John" value={newLead.first_name} onChange={e=>setNewLead({...newLead,first_name:e.target.value})}/></div>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Last Name</label><input style={inp} placeholder="Smith" value={newLead.last_name} onChange={e=>setNewLead({...newLead,last_name:e.target.value})}/></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Email</label><input style={inp} type="email" placeholder="john@company.com" value={newLead.email} onChange={e=>setNewLead({...newLead,email:e.target.value})}/></div>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Phone</label><input style={inp} placeholder="+1 555 000 0000" value={newLead.phone} onChange={e=>setNewLead({...newLead,phone:e.target.value})}/></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Company</label><input style={inp} placeholder="Company name" value={newLead.company} onChange={e=>setNewLead({...newLead,company:e.target.value})}/></div>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Value (₹)</label><input style={inp} type="number" placeholder="0" value={newLead.value} onChange={e=>setNewLead({...newLead,value:e.target.value})}/></div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Source</label><select style={{...inp,background:'#fff'}} value={newLead.source} onChange={e=>setNewLead({...newLead,source:e.target.value})}>{SOURCES.map(s=><option key={s}>{s}</option>)}</select></div>
                <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Stage</label><select style={{...inp,background:'#fff'}} value={newLead.stage} onChange={e=>setNewLead({...newLead,stage:e.target.value})}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></div>
              </div>
              <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Tags <span style={{fontWeight:400,color:C.faint}}>(comma-separated)</span></label><input style={inp} placeholder="hot, SEO, branding" value={newLead.tags} onChange={e=>setNewLead({...newLead,tags:e.target.value})}/></div>
            </div>
            <div style={{padding:'16px 24px',borderTop:`1px solid ${C.border}`,background:'#f9fafb',borderRadius:'0 0 16px 16px',display:'flex',gap:8}}>
              <Btn onClick={handleAdd} disabled={addSaving}><i className="ti ti-user-plus"/>{addSaving?'Saving...':'Add Contact'}</Btn>
              <Btn variant="outline" onClick={()=>setShowAdd(false)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ══ Contact Detail / Edit Modal ═══════════════════════ */}
      {selLead && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}} onClick={closeModal}>
          <div style={{background:'#fff',borderRadius:16,width:'100%',maxWidth:680,maxHeight:'90vh',overflow:'auto',boxShadow:'0 25px 60px rgba(0,0,0,0.25)'}} onClick={e=>e.stopPropagation()}>
            {/* Header */}
            <div style={{background:selLead.dnd?'#6b7280':STAGE_COLORS[selLead.stage]||C.primary,padding:'20px 24px',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'16px 16px 0 0'}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:48,height:48,borderRadius:'50%',background:'rgba(255,255,255,0.25)',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:18,fontWeight:700,flexShrink:0}}>
                  {initials(displayName(selLead))}
                </div>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontWeight:700,fontSize:18,color:'#fff'}}>{displayName(selLead)}</span>
                    {selLead.dnd && (
                      <span style={{background:'#ef4444',color:'#fff',borderRadius:20,padding:'2px 10px',fontSize:11,fontWeight:700,letterSpacing:0.5}}>
                        🚫 DND
                      </span>
                    )}
                  </div>
                  <div style={{fontSize:13,color:'rgba(255,255,255,0.8)'}}>{selLead.company||selLead.email}</div>
                </div>
              </div>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                {!editMode && <>
                  <button onClick={handleEditOpen} style={{background:'rgba(255,255,255,0.2)',border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',color:'#fff',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}><i className="ti ti-edit"/> Edit</button>
                  <button onClick={handleRescore} disabled={aiLoading} style={{background:'rgba(255,255,255,0.2)',border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',color:'#fff',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}><i className="ti ti-brain"/> {aiLoading?'...':'Rescore'}</button>
                  <button onClick={handleDraftEmail} disabled={aiLoading} style={{background:'rgba(255,255,255,0.2)',border:'none',borderRadius:8,padding:'7px 14px',cursor:'pointer',color:'#fff',fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6}}><i className="ti ti-mail"/> {aiLoading?'...':'Draft Email'}</button>
                </>}
                <button onClick={closeModal} style={{background:'rgba(255,255,255,0.2)',border:'none',borderRadius:8,padding:'7px 10px',cursor:'pointer',color:'#fff',fontSize:18,lineHeight:1}}><i className="ti ti-x"/></button>
              </div>
            </div>

            <div style={{padding:24}}>
              {/* ── DND Banner ── */}
              {selLead.dnd && !editMode && (
                <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:'12px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:12}}>
                  <i className="ti ti-ban" style={{fontSize:20,color:C.danger,flexShrink:0}}/>
                  <div style={{flex:1}}>
                    <div style={{fontWeight:600,fontSize:13,color:'#991b1b'}}>Do Not Disturb — Excluded from campaigns</div>
                    {selLead.dnd_reason && <div style={{fontSize:12,color:'#7f1d1d',marginTop:2}}>{selLead.dnd_reason}</div>}
                    {selLead.unsubscribed_at && <div style={{fontSize:11,color:'#7f1d1d',marginTop:2}}>Unsubscribed {fmtDate(selLead.unsubscribed_at)}</div>}
                  </div>
                  <button
                    onClick={() => handleToggleDnd(selLead, false)}
                    disabled={dndLoading}
                    style={{padding:'6px 14px',borderRadius:8,background:'#fff',color:C.danger,cursor:'pointer',fontSize:12,fontWeight:600,border:`1px solid ${C.danger}`,flexShrink:0}}>
                    {dndLoading ? 'Updating...' : 'Remove DND'}
                  </button>
                </div>
              )}

              {!editMode && (
                <>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginBottom:20}}>
                    {[
                      ['First Name', selLead.first_name],
                      ['Last Name',  selLead.last_name],
                      ['Email',      selLead.email],
                      ['Phone',      selLead.phone],
                      ['Company',    selLead.company],
                      ['Source',     selLead.source],
                      ['Stage',      selLead.stage],
                      ['Value',      `₹${(selLead.value||0).toLocaleString()}`],
                      ['AI Score',   selLead.ai_score||'—'],
                      ['Added',      fmtDate(selLead.created_at)],
                    ].map(([k,v])=>(
                      <div key={k} style={{background:'#f9fafb',borderRadius:10,padding:'12px 14px'}}>
                        <div style={{fontSize:10,fontWeight:600,color:'#9ca3af',letterSpacing:0.5,marginBottom:4}}>{k.toUpperCase()}</div>
                        <div style={{fontSize:13,fontWeight:500,color:C.dark}}>{v||'—'}</div>
                      </div>
                    ))}
                  </div>

                  {selLead.tags?.length>0&&(
                    <div style={{marginBottom:20}}>
                      <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:8}}>TAGS</div>
                      <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>{selLead.tags.map(t=><Tag key={t} label={t}/>)}</div>
                    </div>
                  )}

                  {customFields.length>0&&(
                    <div style={{marginBottom:20}}>
                      <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:8}}>CUSTOM FIELDS</div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
                        {customFields.map(f=>(
                          <div key={f.id} style={{background:'#f9fafb',borderRadius:10,padding:'12px 14px'}}>
                            <div style={{fontSize:10,fontWeight:600,color:'#9ca3af',letterSpacing:0.5,marginBottom:4}}>{f.name.toUpperCase()}</div>
                            <div style={{fontSize:13,color:C.dark}}>—</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {aiDraft&&(
                    <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:10,padding:14,marginBottom:20}}>
                      <div style={{fontSize:12,fontWeight:600,color:'#0369a1',marginBottom:8,display:'flex',alignItems:'center',gap:6}}><i className="ti ti-sparkles"/> AI Email Draft</div>
                      <p style={{fontSize:13,color:'#0c4a6e',lineHeight:1.7,whiteSpace:'pre-wrap',margin:0}}>{aiDraft}</p>
                    </div>
                  )}

                  {selLead.notes&&(
                    <div style={{marginBottom:20}}>
                      <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:8}}>NOTES</div>
                      <div style={{background:'#f9fafb',borderRadius:10,padding:'12px 14px',fontSize:13,color:C.dark,lineHeight:1.6,whiteSpace:'pre-wrap'}}>{selLead.notes}</div>
                    </div>
                  )}

                  <div style={{marginBottom:20}}>
                    <div style={{fontSize:11,fontWeight:600,color:C.muted,marginBottom:8}}>MOVE TO STAGE</div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                      {STAGES.map(s=>(
                        <button key={s} onClick={()=>handleStageChange(selLead.id,s)}
                          style={{padding:'6px 14px',borderRadius:20,border:`1px solid ${STAGE_COLORS[s]||C.border}`,background:selLead.stage===s?STAGE_COLORS[s]:'transparent',color:selLead.stage===s?'#fff':STAGE_COLORS[s]||C.text,fontSize:12,fontWeight:selLead.stage===s?600:400,cursor:'pointer',transition:'all 0.15s'}}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* ── DND toggle (enable) when contact is active ── */}
                  {!selLead.dnd && (
                    <div style={{marginBottom:16,padding:'10px 14px',background:'#f9fafb',borderRadius:10,border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:500,color:C.dark}}>Do Not Disturb (DND)</div>
                        <div style={{fontSize:11,color:C.muted}}>Enable to exclude this contact from all campaigns</div>
                      </div>
                      <button onClick={()=>handleToggleDnd(selLead, true)} disabled={dndLoading}
                        style={{padding:'6px 14px',borderRadius:8,border:`1px solid ${C.border}`,background:'#fff',color:C.muted,cursor:'pointer',fontSize:12,fontWeight:500}}>
                        {dndLoading?'Updating...':'Enable DND'}
                      </button>
                    </div>
                  )}

                  <div style={{display:'flex',gap:10}}>
                    <input style={{flex:1,padding:'9px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}}
                      placeholder="Add a note..." value={note} onChange={e=>setNote(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleAddNote()}/>
                    <Btn onClick={handleAddNote}>Add Note</Btn>
                  </div>
                </>
              )}

              {editMode && (
                <>
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>First Name *</label><input style={inp} value={editData.first_name} onChange={e=>setEditData({...editData,first_name:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Last Name</label><input style={inp} value={editData.last_name} onChange={e=>setEditData({...editData,last_name:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Email</label><input style={inp} type="email" value={editData.email} onChange={e=>setEditData({...editData,email:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Phone</label><input style={inp} value={editData.phone} onChange={e=>setEditData({...editData,phone:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Company</label><input style={inp} value={editData.company} onChange={e=>setEditData({...editData,company:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Value (₹)</label><input style={inp} type="number" value={editData.value} onChange={e=>setEditData({...editData,value:e.target.value})}/></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Source</label><select style={{...inp,background:'#fff'}} value={editData.source} onChange={e=>setEditData({...editData,source:e.target.value})}>{SOURCES.map(s=><option key={s}>{s}</option>)}</select></div>
                    <div><label style={{fontSize:12,fontWeight:500,color:C.text}}>Stage</label><select style={{...inp,background:'#fff'}} value={editData.stage} onChange={e=>setEditData({...editData,stage:e.target.value})}>{STAGES.map(s=><option key={s}>{s}</option>)}</select></div>
                  </div>
                  {customFields.length>0&&(
                    <div style={{marginBottom:12}}>
                      <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:10}}>Custom Fields</div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                        {customFields.map(f=>(
                          <div key={f.id}><label style={{fontSize:12,fontWeight:500,color:C.text}}>{f.name}</label><input style={inp} placeholder={f.placeholder||f.name} value={editData[f.field_key]||''} onChange={e=>setEditData({...editData,[f.field_key]:e.target.value})}/></div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{marginBottom:12}}><label style={{fontSize:12,fontWeight:500,color:C.text}}>Tags <span style={{fontWeight:400,color:C.faint}}>(comma-separated)</span></label><input style={inp} placeholder="hot, SEO" value={editData.tags} onChange={e=>setEditData({...editData,tags:e.target.value})}/></div>
                  <div style={{marginBottom:16}}><label style={{fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'}}>Notes</label><textarea rows={3} style={{...inp,resize:'vertical',boxSizing:'border-box',marginTop:4}} value={editData.notes} onChange={e=>setEditData({...editData,notes:e.target.value})}/></div>
                  <div style={{display:'flex',gap:10}}>
                    <Btn onClick={handleEditSave} disabled={saving}><i className="ti ti-device-floppy"/>{saving?'Saving...':'Save Changes'}</Btn>
                    <Btn variant="outline" onClick={()=>setEditMode(false)}>Cancel</Btn>
                  </div>
                </>
              )}
            </div>

            <div style={{padding:'14px 24px',borderTop:`1px solid ${C.border}`,background:'#f9fafb',borderRadius:'0 0 16px 16px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontSize:12,color:C.faint}}>Added {fmtDate(selLead.created_at)}</span>
              <button onClick={e=>handleDelete(selLead.id,e)}
                style={{background:'#FEF2F2',border:'none',borderRadius:8,padding:'6px 14px',cursor:'pointer',color:C.danger,fontSize:12,fontWeight:500,display:'flex',alignItems:'center',gap:6}}>
                <i className="ti ti-trash"/> Delete Contact
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Page Header ─────────────────────────────────────── */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:700,color:C.dark,marginBottom:2}}>Contacts & Leads</h1>
          <p style={{fontSize:13,color:C.muted}}>
            {filtered.length} contact{filtered.length!==1?'s':''}
            {leads.length!==filtered.length?` of ${leads.length} total`:''}
            {dndCount>0&&<span style={{color:C.danger}}> · {dndCount} DND</span>}
            {selectedIds.length>0?` · ${selectedIds.length} selected`:''}
          </p>
        </div>
        <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
          {selectedIds.length>0&&<>
            <Btn variant="ghost" onClick={()=>setShowBulkTag(!showBulkTag)}><i className="ti ti-tag"/> Bulk Tag</Btn>
            <Btn variant="danger" onClick={handleBulkDelete}><i className="ti ti-trash"/> Delete ({selectedIds.length})</Btn>
          </>}
          <Btn variant="ghost" onClick={handleExport}>
            <i className="ti ti-download"/> {selectedIds.length?`Export (${selectedIds.length})`:filtered.length<leads.length?`Export Filtered (${filtered.length})`:'Export All'}
          </Btn>
          <Btn variant="ghost" onClick={()=>setShowImporter(true)}><i className="ti ti-upload"/> Import CSV</Btn>
          <Btn onClick={()=>setShowAdd(true)}><i className="ti ti-plus"/> Add Contact</Btn>
        </div>
      </div>

      {showBulkTag&&(
        <div style={{background:'#f0f9ff',border:'1px solid #bae6fd',borderRadius:10,padding:'12px 16px',marginBottom:14,display:'flex',gap:10,alignItems:'center'}}>
          <i className="ti ti-tag" style={{color:C.primary,fontSize:16}}/>
          <input style={{flex:1,padding:'7px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none'}} placeholder="Tags to add (comma-separated)..." value={bulkTag} onChange={e=>setBulkTag(e.target.value)}/>
          <Btn onClick={handleBulkTag}>Apply to {selectedIds.length} contacts</Btn>
          <Btn variant="ghost" onClick={()=>setShowBulkTag(false)}>Cancel</Btn>
        </div>
      )}

      {/* ── Filters ──────────────────────────────────────────── */}
      <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:'14px 16px',marginBottom:16,display:'flex',gap:10,flexWrap:'wrap',alignItems:'center'}}>
        <div style={{position:'relative',flex:'1 1 200px',minWidth:180}}>
          <i className="ti ti-search" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:C.faint,fontSize:15}}/>
          <input style={{width:'100%',padding:'8px 12px 8px 32px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none',boxSizing:'border-box'}}
            placeholder="Search name, email, company..." value={search} onChange={e=>setSearch(e.target.value)}/>
        </div>
        <select style={{padding:'8px 12px',borderRadius:8,border:`1px solid ${stageFilter?C.primary:C.border}`,fontSize:13,outline:'none',color:stageFilter?C.primary:C.text,background:stageFilter?'var(--color-primary-light)':'#fff',minWidth:130}}
          value={stageFilter} onChange={e=>setStageFilter(e.target.value)}>
          <option value="">All Stages</option>
          {STAGES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select style={{padding:'8px 12px',borderRadius:8,border:`1px solid ${sourceFilter?C.primary:C.border}`,fontSize:13,outline:'none',color:sourceFilter?C.primary:C.text,background:sourceFilter?'var(--color-primary-light)':'#fff',minWidth:140}}
          value={sourceFilter} onChange={e=>setSourceFilter(e.target.value)}>
          <option value="">All Sources</option>
          {SOURCES.map(s=><option key={s}>{s}</option>)}
        </select>
        <select style={{padding:'8px 12px',borderRadius:8,border:`1px solid ${tagFilter?C.primary:C.border}`,fontSize:13,outline:'none',color:tagFilter?C.primary:C.text,background:tagFilter?'var(--color-primary-light)':'#fff',minWidth:120}}
          value={tagFilter} onChange={e=>setTagFilter(e.target.value)}>
          <option value="">All Tags</option>
          {allTags.map(t=><option key={t}>{t}</option>)}
        </select>
        {/* DND filter */}
        <select style={{padding:'8px 12px',borderRadius:8,border:`1px solid ${dndFilter?C.danger:C.border}`,fontSize:13,outline:'none',color:dndFilter?C.danger:C.text,background:dndFilter?'#fef2f2':'#fff',minWidth:110}}
          value={dndFilter} onChange={e=>setDndFilter(e.target.value)}>
          <option value="">All Contacts</option>
          <option value="active">Active Only</option>
          <option value="dnd">🚫 DND Only</option>
        </select>
        {activeFilters>0&&(
          <button onClick={()=>{setStageFilter('');setSourceFilter('');setTagFilter('');setDndFilter('');setSearch('');}}
            style={{padding:'8px 14px',borderRadius:8,border:'none',background:'#FEF2F2',color:C.danger,fontSize:12,cursor:'pointer',fontWeight:500,display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
            <i className="ti ti-x"/> Clear {activeFilters} filter{activeFilters>1?'s':''}
          </button>
        )}
      </div>

      {/* ── Table ────────────────────────────────────────────── */}
      <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,overflow:'hidden'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
          <thead>
            <tr style={{borderBottom:`1px solid ${C.border}`,background:'#f9fafb'}}>
              <th style={{padding:'10px 14px',width:36}}>
                <input type="checkbox" checked={selectedIds.length===filtered.length&&filtered.length>0} onChange={toggleSelectAll} style={{cursor:'pointer',accentColor:C.primary}}/>
              </th>
              {['Name','Company','Source','Stage','AI Score','Value','Added','Actions'].map(h=>(
                <th key={h} style={{padding:'10px 14px',textAlign:'left',color:C.muted,fontWeight:500,fontSize:12}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(l=>(
              <tr key={l.id}
                style={{borderBottom:'1px solid #f3f4f6',cursor:'pointer',background:isSelected(l.id)?'#eff6ff':l.dnd?'#fef2f2':'transparent',transition:'background 0.1s'}}
                onClick={()=>openModal(l)}
                onMouseEnter={e=>{ if(!isSelected(l.id)&&!l.dnd) e.currentTarget.style.background='#f9fafb'; }}
                onMouseLeave={e=>{ e.currentTarget.style.background=isSelected(l.id)?'#eff6ff':l.dnd?'#fef2f2':'transparent'; }}>
                <td style={{padding:'10px 14px'}} onClick={e=>toggleSelect(l.id,e)}>
                  <input type="checkbox" checked={isSelected(l.id)} onChange={()=>{}} style={{cursor:'pointer',accentColor:C.primary}}/>
                </td>
                <td style={{padding:'10px 14px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:30,height:30,borderRadius:'50%',background:l.dnd?'#9ca3af':C.primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700,flexShrink:0}}>
                      {initials(displayName(l))}
                    </div>
                    <div>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontWeight:500}}>{displayName(l)}</span>
                        {l.dnd&&<span style={{background:'#fef2f2',color:C.danger,border:'1px solid #fecaca',borderRadius:4,padding:'1px 5px',fontSize:9,fontWeight:700,letterSpacing:0.3}}>DND</span>}
                      </div>
                      <div style={{fontSize:11,color:C.muted}}>{l.email}</div>
                    </div>
                  </div>
                </td>
                <td style={{padding:'10px 14px',color:C.text}}>{l.company||'—'}</td>
                <td style={{padding:'10px 14px'}}><Tag label={l.source||'—'} color={C.muted}/></td>
                <td style={{padding:'10px 14px'}}><Tag label={l.stage} color={STAGE_COLORS[l.stage]}/></td>
                <td style={{padding:'10px 14px'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <span style={{fontWeight:600,color:scoreColor(l.ai_score||0)}}>{l.ai_score||'—'}</span>
                    {l.ai_score&&<div style={{background:'#f3f4f6',borderRadius:4,height:4,width:48}}><div style={{width:`${l.ai_score}%`,height:4,borderRadius:4,background:scoreColor(l.ai_score)}}/></div>}
                  </div>
                </td>
                <td style={{padding:'10px 14px',fontWeight:500}}>₹{(l.value||0).toLocaleString()}</td>
                <td style={{padding:'10px 14px',color:C.muted,fontSize:12,whiteSpace:'nowrap'}}>{fmtDate(l.created_at)}</td>
                <td style={{padding:'10px 14px'}} onClick={e=>e.stopPropagation()}>
                  <div style={{display:'flex',gap:6}}>
                    <select style={{padding:'4px 8px',borderRadius:6,border:`1px solid ${C.border}`,fontSize:11,outline:'none'}}
                      value={l.stage} onChange={e=>handleStageChange(l.id,e.target.value,e)}>
                      {STAGES.map(s=><option key={s}>{s}</option>)}
                    </select>
                    <button onClick={e=>{e.stopPropagation();openModal(l);setTimeout(()=>setEditMode(true),50);}}
                      style={{padding:'4px 8px',borderRadius:6,border:'none',background:'var(--color-primary-light)',color:C.primary,cursor:'pointer',fontSize:12}}>
                      <i className="ti ti-edit"/>
                    </button>
                    <button onClick={e=>handleDelete(l.id,e)}
                      style={{padding:'4px 8px',borderRadius:6,border:'none',background:'#FEF2F2',color:C.danger,cursor:'pointer',fontSize:12}}>
                      <i className="ti ti-trash"/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!filtered.length&&(
              <tr><td colSpan={9} style={{padding:40,textAlign:'center',color:C.faint,fontSize:13}}>
                {activeFilters>0||search?'No contacts match the current filters.':'No leads found. Add manually or import a CSV file!'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
